import type {
  ApiError,
  ConversationSummary,
  CreatedInvitation,
  DownloadTicket,
  EncryptedEnvelope,
  InvitationPreview,
  JoinResult,
  LoginTokenPreview,
  OwnerProfile,
  ProfileImportResult,
  PublicProfile,
  UploadTicket,
  ChatTheme,
  MessageFrame,
  InterfaceThemeId,
  LanguageCode,
} from '@kairelo/shared';

/**
 * HTTP client.
 *
 * Two rules it exists to keep:
 *   - the owner session travels as an HttpOnly cookie, and the visitor session
 *     as an Authorization header. Neither is ever put in a URL (concept §5);
 *   - error bodies are turned into a typed error so the UI can explain what
 *     happened without ever surfacing a raw server message it did not expect.
 */

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, string>;

  constructor(status: number, body: ApiError | null, fallback: string) {
    super(body?.error.message ?? fallback);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.error.code ?? 'internal';
    this.details = body?.error.details;
  }
}

/** The visitor session credential, held in this browser only. */
let visitorToken: string | null = null;

export function setVisitorToken(token: string | null): void {
  visitorToken = token;
}

export function getVisitorToken(): string | null {
  return visitorToken;
}

async function request<T>(
  path: string,
  init: RequestInit & { visitorAuth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (init.visitorAuth && visitorToken) {
    headers.set('authorization', `Bearer ${visitorToken}`);
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: 'same-origin',
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text.length > 0 ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    throw new ApiRequestError(response.status, body as ApiError | null, 'The request failed.');
  }
  return body as T;
}

/* ------------------------------------------------------------------- auth */

export const auth = {
  requestLoginLink: (email: string) =>
    request<{ status: string }>('/api/auth/login-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /** Read-only. Does not consume the token (concept §3). */
  previewLoginToken: (token: string) =>
    request<LoginTokenPreview>(`/api/auth/login-token?token=${encodeURIComponent(token)}`),

  /** The explicit Continue action. */
  confirm: (token: string) =>
    request<{ owner: { id: string; email: string }; expiresAt: string }>('/api/auth/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  session: () =>
    request<{
      owner: {
        id: string;
        email: string;
        suspendedAt: string | null;
        suspensionReason: string | null;
      };
      limits: { maxActiveConversations: number; maxInvitesPerDay: number; maxUploadBytes: number };
    }>('/api/auth/session'),

  signOut: () => request<{ status: string }>('/api/auth/sign-out', { method: 'POST' }),
};

/* ---------------------------------------------------------------- profile */

export const profile = {
  get: () => request<OwnerProfile>('/api/profile'),

  update: (body: {
    displayName: string;
    headline?: string | null;
    bio?: string | null;
    theme?: ChatTheme;
    preferredInterface?: InterfaceThemeId;
    preferredLanguage?: LanguageCode;
  }) => request<OwnerProfile>('/api/profile', { method: 'PUT', body: JSON.stringify(body) }),

  updateTheme: (theme: ChatTheme) =>
    request<ChatTheme>('/api/profile/theme', { method: 'PUT', body: JSON.stringify(theme) }),

  requestMediaUpload: (contentType: string, sizeBytes: number) =>
    request<{ objectKey: string; uploadUrl: string; requiredContentType: string }>(
      '/api/profile/media',
      { method: 'POST', body: JSON.stringify({ contentType, sizeBytes }) },
    ),

  deleteMedia: (id: string) =>
    request<{ status: string }>(`/api/profile/media/${id}`, { method: 'DELETE' }),

  /**
   * Drafts a profile from a link the owner published about themselves, or from
   * text they pasted. Returns a draft for review — nothing is saved.
   */
  import: (source: { url?: string; text?: string }) =>
    request<ProfileImportResult>('/api/profile/import', {
      method: 'POST',
      body: JSON.stringify(source),
    }),

  /** Downloads one of the images found by an import into the profile bucket. */
  importImage: (url: string) =>
    request<{ objectKey: string }>('/api/profile/import-image', {
      method: 'POST',
      body: JSON.stringify({ url }),
    }),
};

/* ----------------------------------------------------------- invitations */

export const invitations = {
  /**
   * Creates an invitation.
   *
   * `keyStorage` is the client's attestation that it has already generated and
   * persisted a conversation key locally. Without it the server refuses, which
   * is how "do not silently create a link whose key cannot be retained"
   * (concept §5) is enforced on both sides.
   */
  create: (params: { mechanism: 'indexeddb'; label?: EncryptedEnvelope }) =>
    request<CreatedInvitation>('/api/invitations', {
      method: 'POST',
      body: JSON.stringify({
        keyStorage: { persisted: true, mechanism: params.mechanism },
        ...(params.label ? { label: params.label } : {}),
      }),
    }),

  list: () => request<{ conversations: ConversationSummary[] }>('/api/conversations'),

  get: (id: string) => request<ConversationSummary>(`/api/conversations/${id}`),

  /** Brings expiry forward to now; the normal lifecycle does the rest. */
  end: (id: string) =>
    request<ConversationSummary>(`/api/conversations/${id}/end`, { method: 'POST' }),

  messages: (id: string, sinceSeq = 0) =>
    request<{ messages: MessageFrame[] }>(`/api/conversations/${id}/messages?sinceSeq=${sinceSeq}`),
};

/* --------------------------------------------------------------- visitor */

export const visitor = {
  /** Ordinary GET. Never activates or claims (concept §4). */
  preview: (token: string) => request<InvitationPreview>(`/api/invitations/${token}`),

  /** The explicit Join. Claims and activates atomically. */
  join: (token: string, visitorName: EncryptedEnvelope) =>
    request<JoinResult>(`/api/invitations/${token}/join`, {
      method: 'POST',
      body: JSON.stringify({ visitorName }),
    }),

  session: () =>
    request<{
      conversationId: string;
      expiresAt: string;
      activatedAt: string;
      visitorName: EncryptedEnvelope | null;
      profile: PublicProfile;
    }>('/api/visitor/session', { visitorAuth: true }),

  messages: (sinceSeq = 0) =>
    request<{ messages: MessageFrame[] }>(`/api/visitor/messages?sinceSeq=${sinceSeq}`, {
      visitorAuth: true,
    }),
};

/* --------------------------------------------------------------- objects */

export const objects = {
  requestUpload: (conversationId: string, sizeBytes: number) =>
    request<UploadTicket>(`/api/conversations/${conversationId}/objects`, {
      method: 'POST',
      body: JSON.stringify({ sizeBytes }),
      visitorAuth: true,
    }),

  requestDownload: (conversationId: string, objectKey: string) =>
    request<DownloadTicket>(
      `/api/conversations/${conversationId}/objects/${objectKey.split('/').slice(2).join('/')}`,
      { visitorAuth: true },
    ),
};

/* --------------------------------------------------------------- reports */

export const reports = {
  submit: (conversationId: string, body: { reason: string; details?: string }) =>
    request<{ id: string; createdAt: string }>(`/api/conversations/${conversationId}/reports`, {
      method: 'POST',
      body: JSON.stringify(body),
      visitorAuth: true,
    }),
};
